import React from 'react';

import { Icon } from 'design-system-web';
import { useTranslation } from 'react-i18next';

import { DateRangeType } from 'application-types';
import {
  GuestCapacityNoteVariantEnum,
  getGuestCapacityNoteForSlot,
} from 'domains-reservable-availability';
import { ReservableAvailabilityRange } from 'lane-shared/hooks/useReservableAvailabilityByRange';

import styles from './GuestCapacityNote.scss';

const GUEST_CAPACITY_NOTE_TRANSLATION_KEYS: Record<
  GuestCapacityNoteVariantEnum,
  string
> = {
  [GuestCapacityNoteVariantEnum.NoGuestSpots]:
    'shared.content.feature.reservable.guestCapacity.noGuestSpots',
  [GuestCapacityNoteVariantEnum.SingleGuest]:
    'shared.content.feature.reservable.guestCapacity.singleGuest',
  [GuestCapacityNoteVariantEnum.MultipleGuests]:
    'shared.content.feature.reservable.guestCapacity.multipleGuests',
};

type GuestCapacityNoteProps = {
  /** The time slot the member currently has selected, if any. */
  selectedRange?: DateRangeType | null;
  /** Server-computed availability for the day being browsed. */
  availabilityRanges?: ReservableAvailabilityRange[];
  /**
   * Max guests a single booking may invite. Left undefined when guests are not
   * configured for this content, or when headcount capacity is disabled.
   */
  maxGuestsPerBooking?: number;
  /** Total capacity per slot, used when the selection has zero reservations. */
  maxQuantityPerSlot?: number;
};

/**
 * Explains how many guests fit in the selected time slot. Renders nothing when
 * the remaining capacity and guest limit leave nothing worth clarifying.
 */
export function GuestCapacityNote({
  selectedRange,
  availabilityRanges,
  maxGuestsPerBooking,
  maxQuantityPerSlot,
}: GuestCapacityNoteProps) {
  const { t } = useTranslation();
  const note = getGuestCapacityNoteForSlot({
    selectedRange,
    availabilityRanges,
    maxGuestsPerBooking,
    maxQuantityPerSlot,
  });

  if (!note) {
    return null;
  }

  return (
    <div className={styles.GuestCapacityNote} data-test="guestCapacityNote">
      <Icon name="info-circle" className={styles.icon} size="small" />
      <p className={styles.text}>
        {t(GUEST_CAPACITY_NOTE_TRANSLATION_KEYS[note.variant], {
          guestCount: note.invitableGuests,
        })}
      </p>
    </div>
  );
}
